import React, { useEffect, useState } from "react";
import { DomainAssociations, getDomainAssociations, deleteDomainAssociations, postDomainAssociations, patchDomainAssociations, AssociationAction, customErrors } from "api/admin/domainassociations";
import { deleteMembersBulk } from "api/admin/members";
import { Column, Query, QueryResult } from "material-table";
import { DefaultTable, GridTextField } from "components";
import { View } from "features/global/reducer";
import { Club } from "api/clubs";
import { Subscription } from "api/company";
import { useMounted } from "utils/hooks";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { ErrorTypes, isValidVerificationCode } from "../../../../utils/helpers";
import i18n from "../i18n";
import "../b2bfeaturesStyle.scss";
import AttentionBanner from "../AttentionBanner";

interface DomainAssoiationsProps {
    club: Club,
    subscription: Subscription | null,
    onNavigateTo: ({ view, tab }: { view: View, tab?: number }) => void,
}

const DomainAssociationsTab = ({ club, subscription, onNavigateTo }: DomainAssoiationsProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );
    const displayCustomError = (error: any) => enqueueSnackbar(
        `${i18n.customeErrorHandling[error.type]}`,
        { variant: "error" },
    );

    const [isLoading] = useState(false);
    const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
    const [showAddDomainDialog, setShowAddDomainDialog] = useState(false);
    const [showEditDomainDialog, setShowEditDomainDialog] = useState(false);
    const [showVerifyDialog, setShowVerifyDialog] = useState(false);
    const [delDomainAssociation, setDelDomainAssociation] = useState<DomainAssociations | undefined>(undefined);
    const [editDomainAssociation, setEditDomainAssociation] = useState<DomainAssociations | undefined>(undefined);
    const [verifyDomainAssociation, setVerifyDomainAssociation] = useState<DomainAssociations | undefined>(undefined);
    const [verificationEmail, setVerificationEmail] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [associationAction, setAssociationAction] = useState<AssociationAction | undefined>(undefined);

    const addNewDomainAssociation = () => {
        postDomainAssociations({ clubId: club.id, verificationEmail })
            .then(() => refreshDomainAssociationsTable())
            .catch((error) => {
                if (customErrors.includes(error.type)) {
                    displayCustomError(error);
                } else {
                    displayError(error);
                }
            }).finally(() => {
                setShowAddDomainDialog(false);
                setVerificationEmail("");
            });
    };

    const resendVerification = () => {
        patchDomainAssociations({ associationId: verifyDomainAssociation!.id, resend: true })
            .then((data) => {
                enqueueSnackbar(i18n.verifiationMailSent, { variant: "success" });
            })
            .catch((error) => displayError(error));
    };

    const submitVerifcation = () => {
        patchDomainAssociations({ associationId: verifyDomainAssociation!.id, verificationCode })
            .then((data) => {
                setShowVerifyDialog(false);
                refreshDomainAssociationsTable();
            })
            .catch((error) => displayError(error))
            .finally(() => {
                setShowVerifyDialog(false);
                setVerificationCode("");
            });
    };

    const updateDomainAssociation = () => {
        patchDomainAssociations({ associationId: editDomainAssociation!.id, associationAction })
            .then((data) => {
                setShowEditDomainDialog(false);
                refreshDomainAssociationsTable();
            })
            .catch((error) => displayError(error))
            .finally(() => {
                setShowEditDomainDialog(false);
                setAssociationAction(undefined);
            });
    };

    const defaultData = {
        data: [],
        page: 0,
        totalCount: 0,
    };

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<DomainAssociations>) => {
        // paging
        const params: Parameters<typeof getDomainAssociations>[0] = {
            clubId: club.id,
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };
        return params;
    };

    const tableQuery = (query: Query<DomainAssociations>): Promise<QueryResult<DomainAssociations>> => new Promise((resolve) => {
        const params = convertQueryToParams(query);
        getDomainAssociations(params)
            .then((response) => {
                resolve({
                    ...response,
                    page: query.page,
                });
            })
            .catch((error) => {
                if (error.type === ErrorTypes.CLUB_IN_DEMO_MODE) {
                    enqueueSnackbar(i18n.demoModeErrorMsg, { variant: "error" });
                } else {
                    displayError(error);
                }
                resolve(defaultData);
            });
    });

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshDomainAssociationsTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange(); // execute query again
    };
    useEffect(refreshDomainAssociationsTable, [club]);

    const tableActions = [
        (domainAssociation: DomainAssociations) => ({
            icon: "edit",
            tooltip: i18n.edit,
            onClick: () => {
                setEditDomainAssociation(domainAssociation);
                setShowEditDomainDialog(true);
            },
        }),
        {
            disabled: !club.id || club.clubStatus === "demo",
            icon: "add_box",
            isFreeAction: true,
            tooltip: i18n.addNew,
            onClick: () => setShowAddDomainDialog(true),
        },
        (domainAssociation: DomainAssociations) => ({
            disabled: !club.id,
            icon: "delete",
            tooltip: i18n.delete,
            onClick: () => {
                setDelDomainAssociation(domainAssociation);
                setShowDeleteAlertDialog(true);
            },
        }),
        (domainAssociation: DomainAssociations) => ({
            hidden: domainAssociation.associationStatus !== "pending",
            icon: "check_circle_outline",
            tooltip: i18n.verify,
            onClick: () => {
                setVerifyDomainAssociation(domainAssociation);
                setShowVerifyDialog(true);
            },
        }),
    ];

    const tableColumns: Column<DomainAssociations>[] = [
        {
            editable: "never",
            field: "domain",
            title: i18n.domain,
        },
        {
            editable: "never",
            field: "verificationEmail",
            title: i18n.verificationEmail,
        },
        {
            editable: "never",
            field: "associationStatus",
            title: i18n.associationStatus,
            render: (rowData: DomainAssociations) => i18n.verificationStatus[rowData.associationStatus],
        },
        {
            field: "associationAction",
            lookup: ["none", "request", "auto-add"].reduce<{ [name: string]: string }>((acc, elem) => {
                acc[elem] = i18n.associationActions[elem];
                return acc;
            }, {}),
            title: i18n.associationAction,
        },
    ];

    const onRowDelete = (oldData: DomainAssociations) => new Promise<void>((resolve) => {
        deleteDomainAssociations({
            associationId: oldData.id,
        })
            .then(() => {
                resolve();
                refreshDomainAssociationsTable();
            })
            .catch((err) => {
                displayError(err);
                resolve();
            });
    });

    const BulkDelete = (oldData: DomainAssociations) => new Promise<void>((resolve) => {
        deleteDomainAssociations({
            associationId: oldData.id,
        }).then(() => {
            deleteMembersBulk({ clubId: club.id, query: `@${oldData.domain}` }).then(() => {
                resolve();
                refreshDomainAssociationsTable();
            }).catch((err) => {
                displayError(err);
                resolve();
            });
        }).catch((err) => {
            displayError(err);
            resolve();
        });
    });

    // render
    return (
        <>
            {(subscription === null || club.clubStatus === "demo") && (
                <Box className="boxContainer">
                    <AttentionBanner club={club} onNavigateTo={onNavigateTo} />
                </Box>
            )}

            <Box className="boxContainer">
                <Grid container>
                    <Grid item xs={12} md={12}>
                        <Typography align="left" variant="h6">{i18n.domainAssociations}</Typography>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Typography align="left">{i18n.domainPageMessage}</Typography>
                    </Grid>
                    <Grid item xs={12} md={12}>&nbsp;</Grid>
                    <Grid item xs={12} md={12}>
                        <DefaultTable
                            actions={tableActions}
                            columns={tableColumns}
                            data={tableQuery}
                            isLoading={isLoading}
                            tableRef={tableRef}
                            options={{ search: false, selection: false, sorting: false }}
                            title=""
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Add Dialog */}
            <Dialog open={showAddDomainDialog} onClose={() => setShowAddDomainDialog(false)} fullWidth>
                <DialogTitle>{i18n.createAssociation}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <GridTextField
                                label={i18n.verificationEmail}
                                size={12}
                                value={verificationEmail}
                                onChange={(v) => setVerificationEmail(v)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setShowAddDomainDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={addNewDomainAssociation}>
                        {i18n.confirm}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={showEditDomainDialog} onClose={() => setShowEditDomainDialog(false)} fullWidth>
                <DialogTitle>{i18n.editAssociation}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {editDomainAssociation && (
                            <>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>{i18n.associationAction}</InputLabel>
                                        <Select
                                            label={i18n.associationAction}
                                            value={associationAction || editDomainAssociation.associationAction}
                                            onChange={(e) => setAssociationAction(e.target.value as AssociationAction)}
                                        >
                                            <MenuItem value="auto-add">{i18n.associationActions["auto-add"]}</MenuItem>
                                            <MenuItem value="request">{i18n.associationActions.request}</MenuItem>
                                            <MenuItem value="none">{i18n.associationActions.none}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setShowEditDomainDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={updateDomainAssociation}>
                        {i18n.confirm}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Verify Dialog */}
            <Dialog open={showVerifyDialog} onClose={() => setShowVerifyDialog(false)} fullWidth>
                <DialogTitle>{i18n.verifyDomain}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {verifyDomainAssociation && (
                            <>
                                <Grid item xs={12}>
                                    <GridTextField
                                        label={i18n.verificationCode}
                                        size={12}
                                        value={verificationCode}
                                        onChange={(v) => setVerificationCode(v)}
                                        error={verificationCode !== "" && isValidVerificationCode(verificationCode)}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setShowVerifyDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={resendVerification}>
                        {i18n.resend}
                    </Button>
                    <Button color="primary" onClick={submitVerifcation} disabled={verificationCode !== "" && isValidVerificationCode(verificationCode)}>
                        {i18n.confirm}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={showDeleteAlertDialog} onClose={() => setShowDeleteAlertDialog(false)}>
                <DialogTitle>{i18n.alert}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{i18n.deleteMessage(delDomainAssociation)}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setShowDeleteAlertDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="default" onClick={() => BulkDelete(delDomainAssociation!).then(() => setShowDeleteAlertDialog(false))}>
                        {i18n.deleteDomainWithMember}
                    </Button>
                    <Button color="primary" onClick={() => onRowDelete(delDomainAssociation!).then(() => setShowDeleteAlertDialog(false))}>
                        {i18n.deleteOnlyDomain}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DomainAssociationsTab;
